import { useEffect, useState, useCallback } from 'react'
import {
  Lightbulb,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  PiggyBank,
  ShieldCheck,
  Receipt,
  TrendingUp,
  Sparkles,
  CalendarClock,
  Scale,
} from 'lucide-react'
import { getInsightsService } from '@/services/insights'
import { useAuth } from '@/contexts/AuthContext'
import {
  INSIGHT_TYPE_LABELS,
  INSIGHT_STATUS_LABELS,
  type Insight,
  type InsightType,
  type InsightStatus,
} from '@/types/insight.types'

const TYPE_ICON: Record<InsightType, typeof Lightbulb> = {
  tax_saving: PiggyBank,
  compliance: ShieldCheck,
  deduction: Receipt,
  investment: TrendingUp,
  risk_alert: AlertTriangle,
  optimization: Sparkles,
  deadline: CalendarClock,
  regulatory: Scale,
}

const TYPE_TINT: Record<InsightType, string> = {
  tax_saving: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  compliance: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  deduction: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  investment: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  risk_alert: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  optimization: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  deadline: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',
  regulatory: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
}

const STATUS_TINT: Record<InsightStatus, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  applied: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  dismissed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  expired: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  pending_review: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',
}

interface CustomerInsightsSectionProps {
  /** UI customer id (e.g. `cust-001`). */
  customerId: string
}

export default function CustomerInsightsSection({
  customerId,
}: CustomerInsightsSectionProps) {
  const { hasPermission } = useAuth()
  const canGenerate = hasPermission('txh.insight.generate')
  const canDismiss = hasPermission('txh.insight.dismiss')
  const canApply = hasPermission('txh.insight.apply')
  const service = getInsightsService()

  const [insights, setInsights] = useState<Insight[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await service.listForCustomer(customerId)
      setInsights(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load insights.')
    } finally {
      setLoading(false)
    }
  }, [service, customerId])

  useEffect(() => {
    load()
  }, [load])

  const handleGenerate = async () => {
    if (!canGenerate) return
    setGenerating(true)
    setError(null)
    try {
      await service.generate({ customerId, forceRefresh: true })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate insights.')
    } finally {
      setGenerating(false)
    }
  }

  const updateStatus = (updated: Insight) =>
    setInsights((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))

  const handleDismiss = async (id: string) => {
    if (!canDismiss) return
    setBusyId(id)
    try {
      const updated = await service.dismiss(id)
      updateStatus(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not dismiss insight.')
    } finally {
      setBusyId(null)
    }
  }

  const handleApply = async (id: string) => {
    if (!canApply) return
    setBusyId(id)
    try {
      const updated = await service.apply(id)
      updateStatus(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not apply insight.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="card card-elevated p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-primary-500" />
          Insights &amp; Recommendations
        </h3>
        {canGenerate && (
          <button
            onClick={handleGenerate}
            disabled={generating || loading}
            className="btn btn-primary btn-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating…' : 'Generate Insights'}
          </button>
        )}
      </div>

      <div role="note" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <span className="font-semibold">AI Integration Preview — </span>
          <span>Insights shown are generated from sample data for demonstration purposes. Live AI-powered insights tailored to each customer's tax profile will be available in a future release. These are not tax advice; verify every recommendation before acting.</span>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-text-tertiary">
          <span className="loading loading-spinner loading-sm" />
        </div>
      ) : insights.length === 0 ? (
        <div className="text-center py-10 space-y-3">
          <div className="w-12 h-12 bg-surface-overlay rounded-full flex items-center justify-center mx-auto">
            <Lightbulb className="w-6 h-6 text-text-tertiary" />
          </div>
          <p className="text-sm font-medium text-text-primary">No insights yet</p>
          <p className="text-sm text-text-secondary">
            Generate AI-driven recommendations for this customer.
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {insights.map((insight) => {
            const Icon = TYPE_ICON[insight.insightType] ?? Lightbulb
            const tint = TYPE_TINT[insight.insightType] ?? TYPE_TINT.optimization
            const isBusy = busyId === insight.id
            const isActionable =
              insight.status === 'active' || insight.status === 'pending_review'
            return (
              <li
                key={insight.id}
                className="border border-surface-overlay rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start gap-3">
                  <span className={`p-2 rounded-lg flex-shrink-0 ${tint}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-text-primary">
                        {insight.title}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tint}`}>
                        {INSIGHT_TYPE_LABELS[insight.insightType] ?? insight.insightType}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_TINT[insight.status] ?? STATUS_TINT.expired
                        }`}
                      >
                        {INSIGHT_STATUS_LABELS[insight.status] ?? insight.status}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary">{insight.description}</p>
                    <p className="text-xs text-text-tertiary">
                      Confidence {Math.round(insight.confidenceScore * 100)}%
                    </p>
                  </div>
                </div>

                {insight.recommendations.length > 0 && (
                  <ul className="space-y-1 pl-1">
                    {insight.recommendations.map((rec, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-text-secondary"
                      >
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {isActionable && (canDismiss || canApply) && (
                  <div className="flex items-center justify-end gap-2 pt-1">
                    {canDismiss && (
                      <button
                        onClick={() => handleDismiss(insight.id)}
                        disabled={isBusy}
                        className="btn btn-ghost btn-sm flex items-center gap-1"
                      >
                        <X className="w-4 h-4" />
                        Dismiss
                      </button>
                    )}
                    {canApply && (
                      <button
                        onClick={() => handleApply(insight.id)}
                        disabled={isBusy}
                        className="btn btn-outline btn-sm flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        Apply
                      </button>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
